/**
 * Costing Integration for Subcontracting
 * Handles cost tracking, variance analysis, and job costing updates
 */

import { supabase } from '@/lib/supabase/client';
import { 
  SubcontractDispatchedEvent, 
  SubcontractReceivedEvent, 
  SubcontractClosedEvent,
  subcontractingEvents 
} from '@/lib/events/subcontractingEvents';

export interface JobCostingEntry {
  id?: string;
  job_card_id: string;
  cost_type: 'material' | 'labor' | 'overhead' | 'subcontract_service' | 'subcontract_transport';
  cost_category: 'standard' | 'actual' | 'variance';
  amount: number;
  currency: string;
  reference_number?: string;
  reference_type?: string;
  reference_id?: string;
  description?: string;
  created_by?: string;
  created_at?: string;
}

export interface CostVariance {
  jobCardId: string;
  jobCardNumber: string;
  standardCost: number;
  actualCost: number;
  variance: number;
  variancePercentage: number;
  varianceType: 'favorable' | 'unfavorable';
}

class CostingIntegration {
  private defaultCurrency = 'INR';

  /**
   * Handle subcontract dispatch - Record standard costs
   */
  async handleSubcontractDispatch(event: SubcontractDispatchedEvent): Promise<void> {
    try {
      console.log('💰 Processing costing for subcontract dispatch:', event.subContractNumber);

      // Get planned costs from routing operations
      const plannedCosts = await this.getPlannedSubcontractCosts(event.data.operationIds);

      const costEntries: JobCostingEntry[] = [];

      // Record standard costs for each job card
      for (const jobCardId of event.data.jobCardIds) {
        const jobCardPlannedCost = plannedCosts.find(cost => 
          event.data.operationIds.includes(cost.operationId)
        );

        if (jobCardPlannedCost) {
          costEntries.push({
            job_card_id: jobCardId,
            cost_type: 'subcontract_service',
            cost_category: 'standard',
            amount: jobCardPlannedCost.serviceCost,
            currency: this.defaultCurrency,
            reference_number: event.subContractNumber,
            reference_type: 'sub_contract',
            reference_id: event.subContractId,
            description: `Standard subcontract service cost for ${event.vendorName}`,
            created_at: event.timestamp
          });

          // Record transport cost if applicable
          if (jobCardPlannedCost.transportCost > 0) {
            costEntries.push({
              job_card_id: jobCardId,
              cost_type: 'subcontract_transport',
              cost_category: 'standard',
              amount: jobCardPlannedCost.transportCost,
              currency: this.defaultCurrency,
              reference_number: event.subContractNumber,
              reference_type: 'sub_contract',
              reference_id: event.subContractId,
              description: `Standard transport cost for ${event.vendorName}`,
              created_at: event.timestamp
            });
          }
        }
      }

      // Insert cost entries
      if (costEntries.length > 0) {
        const { error } = await supabase
          .from('job_costing')
          .insert(costEntries);

        if (error) {
          console.error('Error creating cost entries:', error);
          throw error;
        }

        console.log(`✅ Created ${costEntries.length} standard cost entries`);
      }

    } catch (error) {
      console.error('Error in handleSubcontractDispatch:', error);
      throw error;
    }
  }

  /**
   * Handle subcontract receipt - Update progress costs
   */
  async handleSubcontractReceipt(event: SubcontractReceivedEvent): Promise<void> {
    try {
      console.log('📊 Processing costing for subcontract receipt:', event.subContractNumber);

      // TODO: Add costing_status field to job_cards table
      // For now, just log the status update
      console.log(`ℹ️ Would update costing status to 'subcontract_work_completed' for ${event.data.jobCardIds.length} job cards`);
      console.log(`ℹ️ (costing_status field not yet added to job_cards table)`);

      // Update job card completion status for costing
      // for (const jobCardId of event.data.jobCardIds) {
      //   await supabase
      //     .from('job_cards')
      //     .update({
      //       costing_status: 'subcontract_work_completed',
      //       updated_at: event.timestamp
      //     })
      //     .eq('id', jobCardId);
      // }

      // console.log(`✅ Updated costing status for ${event.data.jobCardIds.length} job cards`);

    } catch (error) {
      console.error('Error in handleSubcontractReceipt:', error);
      throw error;
    }
  }

  /**
   * Handle subcontract closure - Record actual costs and calculate variances
   */
  async handleSubcontractClosure(event: SubcontractClosedEvent): Promise<void> {
    try {
      console.log('🔄 Processing costing for subcontract closure:', event.subContractNumber);

      const costEntries: JobCostingEntry[] = [];
      const variances: CostVariance[] = [];

      // Calculate costs per job card
      const costPerJobCard = event.data.totalServiceCost / event.data.jobCardIds.length;
      const additionalCostPerJobCard = event.data.additionalCosts / event.data.jobCardIds.length;
      const transportCostPerJobCard = event.data.transportCost / event.data.jobCardIds.length;

      for (const jobCardId of event.data.jobCardIds) {
        // Get job card details
        const { data: jobCard } = await supabase
          .from('job_cards')
          .select('job_card_number')
          .eq('id', jobCardId)
          .single();

        // Record actual service cost
        costEntries.push({
          job_card_id: jobCardId,
          cost_type: 'subcontract_service',
          cost_category: 'actual',
          amount: costPerJobCard,
          currency: this.defaultCurrency,
          reference_number: event.subContractNumber,
          reference_type: 'sub_contract',
          reference_id: event.subContractId,
          description: `Actual subcontract service cost for ${event.vendorName}`,
          created_at: event.timestamp
        });

        // Record additional costs if any
        if (additionalCostPerJobCard > 0) {
          costEntries.push({
            job_card_id: jobCardId,
            cost_type: 'subcontract_service',
            cost_category: 'actual',
            amount: additionalCostPerJobCard,
            currency: this.defaultCurrency,
            reference_number: event.subContractNumber,
            reference_type: 'sub_contract',
            reference_id: event.subContractId,
            description: `Additional service cost for ${event.vendorName}`,
            created_at: event.timestamp
          });
        }

        // Record transport cost if any
        if (transportCostPerJobCard > 0) {
          costEntries.push({
            job_card_id: jobCardId,
            cost_type: 'subcontract_transport',
            cost_category: 'actual',
            amount: transportCostPerJobCard,
            currency: this.defaultCurrency,
            reference_number: event.subContractNumber,
            reference_type: 'sub_contract',
            reference_id: event.subContractId,
            description: `Actual transport cost for ${event.vendorName}`,
            created_at: event.timestamp
          });
        }

        // Calculate variance
        const variance = await this.calculateCostVariance(jobCardId, event.subContractId);
        if (variance) {
          variances.push(variance);

          // Record variance entry
          if (variance.variance !== 0) {
            costEntries.push({
              job_card_id: jobCardId,
              cost_type: 'subcontract_service',
              cost_category: 'variance',
              amount: variance.variance,
              currency: this.defaultCurrency,
              reference_number: event.subContractNumber,
              reference_type: 'sub_contract',
              reference_id: event.subContractId,
              description: `Cost variance (${variance.varianceType}) - ${variance.variancePercentage.toFixed(2)}%`,
              created_at: event.timestamp
            });
          }
        }
      }

      // Insert cost entries
      if (costEntries.length > 0) {
        const { error } = await supabase
          .from('job_costing')
          .insert(costEntries);

        if (error) {
          console.error('Error creating cost entries:', error);
          throw error;
        }

        console.log(`✅ Created ${costEntries.length} actual cost and variance entries`);
      }

      // Update job card final costing status
      await supabase
        .from('job_cards')
        .update({
          costing_status: 'subcontract_costs_finalized',
          updated_at: event.timestamp
        })
        .in('id', event.data.jobCardIds);

      console.log(`✅ Finalized costing for ${event.data.jobCardIds.length} job cards`);

      // Log variances for reporting
      if (variances.length > 0) {
        console.log('📈 Cost Variances:', variances);
      }

    } catch (error) {
      console.error('Error in handleSubcontractClosure:', error);
      throw error;
    }
  }

  /**
   * Get planned subcontract costs from routing operations
   */
  private async getPlannedSubcontractCosts(operationIds: string[]): Promise<Array<{
    operationId: string;
    serviceCost: number;
    transportCost: number;
  }>> {
    try {
      const { data, error } = await supabase
        .from('routing_operations')
        .select('id, subcontract_service_cost, subcontract_transport_cost')
        .in('id', operationIds);

      if (error) throw error;

      return data?.map(op => ({
        operationId: op.id,
        serviceCost: op.subcontract_service_cost || 0,
        transportCost: op.subcontract_transport_cost || 0
      })) || [];
    } catch (error) {
      console.error('Error fetching planned costs:', error);
      return [];
    }
  }

  /**
   * Calculate cost variance for a job card
   */
  private async calculateCostVariance(jobCardId: string, subContractId: string): Promise<CostVariance | null> {
    try {
      // Get standard and actual costs
      const { data: costs, error } = await supabase
        .from('job_costing')
        .select('cost_category, amount')
        .eq('job_card_id', jobCardId)
        .eq('reference_id', subContractId)
        .in('cost_category', ['standard', 'actual']);

      if (error) throw error;

      const standardCost = costs?.filter(c => c.cost_category === 'standard')
        .reduce((sum, c) => sum + c.amount, 0) || 0;
      
      const actualCost = costs?.filter(c => c.cost_category === 'actual')
        .reduce((sum, c) => sum + c.amount, 0) || 0;

      if (standardCost === 0 && actualCost === 0) return null;

      const variance = actualCost - standardCost;
      const variancePercentage = standardCost > 0 ? (variance / standardCost) * 100 : 0;

      // Get job card number
      const { data: jobCard } = await supabase
        .from('job_cards')
        .select('job_card_number')
        .eq('id', jobCardId)
        .single();

      return {
        jobCardId,
        jobCardNumber: jobCard?.job_card_number || jobCardId,
        standardCost,
        actualCost,
        variance,
        variancePercentage,
        varianceType: variance > 0 ? 'unfavorable' : 'favorable'
      };
    } catch (error) {
      console.error('Error calculating cost variance:', error);
      return null;
    }
  }

  /**
   * Get cost summary for a sub-contract
   */
  async getSubcontractCostSummary(subContractId: string): Promise<{
    totalStandardCost: number;
    totalActualCost: number;
    totalVariance: number;
    costsByJobCard: Array<{
      jobCardId: string;
      jobCardNumber: string;
      standardCost: number;
      actualCost: number;
      variance: number;
    }>;
  }> {
    try {
      const { data, error } = await supabase
        .from('job_costing')
        .select(`
          job_card_id,
          cost_category,
          amount,
          job_cards!inner(job_card_number)
        `)
        .eq('reference_id', subContractId);

      if (error) throw error;

      const costsByJobCard = new Map();
      let totalStandardCost = 0;
      let totalActualCost = 0;

      data?.forEach(cost => {
        const jobCardId = cost.job_card_id;
        if (!costsByJobCard.has(jobCardId)) {
          costsByJobCard.set(jobCardId, {
            jobCardId,
            jobCardNumber: cost.job_cards.job_card_number,
            standardCost: 0,
            actualCost: 0,
            variance: 0
          });
        }

        const jobCardCost = costsByJobCard.get(jobCardId);
        if (cost.cost_category === 'standard') {
          jobCardCost.standardCost += cost.amount;
          totalStandardCost += cost.amount;
        } else if (cost.cost_category === 'actual') {
          jobCardCost.actualCost += cost.amount;
          totalActualCost += cost.amount;
        }
      });

      // Calculate variances
      const costsByJobCardArray = Array.from(costsByJobCard.values()).map(cost => ({
        ...cost,
        variance: cost.actualCost - cost.standardCost
      }));

      return {
        totalStandardCost,
        totalActualCost,
        totalVariance: totalActualCost - totalStandardCost,
        costsByJobCard: costsByJobCardArray
      };
    } catch (error) {
      console.error('Error getting cost summary:', error);
      return {
        totalStandardCost: 0,
        totalActualCost: 0,
        totalVariance: 0,
        costsByJobCard: []
      };
    }
  }
}

// Global costing integration instance
export const costingIntegration = new CostingIntegration();

// Register event listeners
subcontractingEvents.on('subcon.dispatched', (event) => {
  return costingIntegration.handleSubcontractDispatch(event);
});

subcontractingEvents.on('subcon.received', (event) => {
  return costingIntegration.handleSubcontractReceipt(event);
});

subcontractingEvents.on('subcon.closed', (event) => {
  return costingIntegration.handleSubcontractClosure(event);
});

export default costingIntegration;
